import React from 'react'
import { Box, Button } from '@mui/material'
import { Add } from '@mui/icons-material'
import Item from './Item'
import { DndProvider } from 'react-dnd'
import { TouchBackend } from 'react-dnd-touch-backend'
import { HTML5Backend } from 'react-dnd-html5-backend'
import useIsMobile from '../../hooks/useIsMobile'

const ListEditor = ({
  maxLength = 25,
  list,
  editableKeys = [{ label: 'Name', key: 'name' }],
  onChange,
  getCanRemove = () => true,
  getCanEdit = () => true,
  getCanEditKey = () => true,
  getAltText = () => null,
  emptyItem,
  getEmptyItem,
  disabled,
  allowDuplicates,
  removeOptions,
  getItemOnRemove,
  canUndoRemove,
  canReorder = false,
  canAddOnEnter = false,
  dragOnly,
  displayOverride,
}) => {
  const generateEmptyItem = () => {
    if (emptyItem) return emptyItem
    if (getEmptyItem) return getEmptyItem()

    return editableKeys.reduce(
      (acc, { key, defaultVal }) => ({
        ...acc,
        [key]: defaultVal || '',
      }),
      {}
    )
  }

  const addItem = () => {
    if (list.length >= maxLength) return
    onChange([...list, generateEmptyItem()])
  }

  const addItemOnEnter = () => {
    if (canAddOnEnter && list.length < maxLength) {
      const length = list.length

      addItem()

      setTimeout(() => {
        const el = document.getElementById(`list-editor-key-${length}`)
        if (el) el.focus()
      }, 50)
    }
  }

  const editItem = (item, index, key, value) => {
    onChange([
      ...list.slice(0, index),
      { ...item, [key]: value },
      ...list.slice(index + 1),
    ])
  }

  const mergeEditItem = (item, index) => {
    onChange([...list.slice(0, index), { ...item }, ...list.slice(index + 1)])
  }

  const setItem = (item, index) => {
    onChange([...list.slice(0, index), item, ...list.slice(index + 1)])
  }

  const removeItem = (i) => {
    const newList = [...list]
    newList.splice(i, 1)
    onChange(newList)
  }

  const onDrop = (item, id) => {
    const filtered = list.filter((li) => li.id !== item.id)

    const ids = filtered.map((li) => li.id)
    const insertAfterIndex = ids.indexOf(id)

    const spliced = filtered
    spliced.splice(insertAfterIndex + 1, 0, item)

    onChange(spliced)
  }

  const width = `calc(${100 / editableKeys.length}% - ${
    8 * editableKeys.length
  }px)`

  const isMobile = useIsMobile()

  return (
    <>
      <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
        <Box>
          {list.map((item, i) => {
            const canEdit = !disabled && getCanEdit(item, i)
            const canRemove = getCanRemove(item, i)
            const altText = getAltText(item, i)

            return (
              <Item
                key={i}
                canReorder={canReorder}
                canRemove={canRemove}
                altText={altText}
                canEdit={canEdit}
                i={i}
                editableKeys={editableKeys}
                getCanEditKey={getCanEditKey}
                item={item}
                width={width}
                allowDuplicates={allowDuplicates}
                removeOptions={removeOptions}
                getItemOnRemove={getItemOnRemove}
                canUndoRemove={canUndoRemove}
                disabled={disabled}
                removeItem={removeItem}
                setItem={setItem}
                editItem={editItem}
                mergeEditItem={mergeEditItem}
                onDrop={onDrop}
                list={list}
                addItemOnEnter={addItemOnEnter}
                displayOverride={displayOverride}
                dragOnly={dragOnly}
              />
            )
          })}
        </Box>
        {!disabled && !dragOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              color={'primary'}
              onClick={addItem}
              endIcon={<Add />}
              disabled={list.length >= maxLength || disabled}
              sx={{ mt: 1 }}
            >
              Add {list.length > 0 && 'another'}
            </Button>
          </Box>
        )}
      </DndProvider>
    </>
  )
}

export default ListEditor
